import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    SimpleButton,
    Container,
    ErrorMessage,
    GradeLevelIcon,
    UserIcon,
    UsernameIcon,
    OrganizationsIcon,
    StatusMessage,
} from '@/src/ui';
import { apiRequest } from '@/src/services';
import { useAuth, type UserInfo } from '@/src/app/context/useAuth';
import { sanitizeGradeShortCode } from '@/src/utils';

// Mapping of Grade Short Codes to their CSS Light Variables for display
const GRADE_OPTS = [
    { code: 'tk', cssVar: '--pm-gtk-light' },
    { code: 'k', cssVar: '--pm-gk-light' },
    { code: '1', cssVar: '--pm-g1-light' },
    { code: '2', cssVar: '--pm-g2-light' },
    { code: '3', cssVar: '--pm-g3-light' },
    { code: '4', cssVar: '--pm-g4-light' },
    { code: '5', cssVar: '--pm-g5-light' },
    { code: '6', cssVar: '--pm-g6-light' },
    { code: '6m', cssVar: '--pm-g6m-light' },
    { code: '7', cssVar: '--pm-g7-light' },
    { code: '8', cssVar: '--pm-g8-light' },
];

interface UserProfileDTO {
    id: string;
    username: string;
    gradeShortName: string;
    name: string;
    districtName: string;
    schoolName: string;
}

const UserProfilePage: React.FC = () => {
    document.title = 'User Profile';

    const navigate = useNavigate();
    const { user, login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Color Selection State
    const [selectedShortCode, setSelectedShortCode] = useState<string>('');
    const [colorSuccess, setColorSuccess] = useState(false);

    // Split options for layout: 6 in first row, remaining (5) in second
    const row1 = GRADE_OPTS.slice(0, 6);
    const row2 = GRADE_OPTS.slice(6);

    const [userData, setUserData] = useState<UserProfileDTO | null>(null);

    // Sync state with Context AND LocalStorage on mount/change
    useEffect(() => {
        if (user) {
            const storageKey = `grade_theme_override_${user.id}`;
            const storedOverride = localStorage.getItem(storageKey) || '';

            const sanitizedStored = sanitizeGradeShortCode(storedOverride);
            const sanitizedContextOverride = sanitizeGradeShortCode(
                user.alternateGradeColorShortCode
            );
            const sanitizedDefault = sanitizeGradeShortCode(user.gradeShortCode);

            const activeCode =
                sanitizedStored || sanitizedContextOverride || sanitizedDefault || '';

            setSelectedShortCode(activeCode);

            if (sanitizedStored && sanitizedStored !== sanitizedContextOverride) {
                const updatedUser: UserInfo = {
                    ...user,
                    alternateGradeColorShortCode: sanitizedStored,
                };
                login(updatedUser);
            }
        }
    }, [user, login]);

    const handleColorSubmit = () => {
        setColorSuccess(false);

        if (user) {
            const storageKey = `grade_theme_override_${user.id}`;
            const sanitizedSelection = sanitizeGradeShortCode(selectedShortCode);

            localStorage.setItem(storageKey, sanitizedSelection);

            const updatedUser: UserInfo = {
                ...user,
                alternateGradeColorShortCode: sanitizedSelection,
            };

            login(updatedUser);

            setColorSuccess(true);
            setTimeout(() => setColorSuccess(false), 3000);
        }
    };

    useEffect(() => {
        (async function init() {
            setError('');
            setLoading(true);
            try {
                const res = await apiRequest<UserProfileDTO>('GET', 'dbo/users/userProfileInfo');
                setUserData(res.data);
            } catch {
                setError('Failed to fetch user data');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const renderColorButton = (opt: { code: string; cssVar: string }) => (
        <button
            key={opt.code}
            onClick={() => setSelectedShortCode(opt.code)}
            type="button"
            title={opt.code}
            style={{ backgroundColor: `var(${opt.cssVar})` }}
            className={`
                w-8 h-8 rounded-full border-2 transition-all duration-200 flex items-center justify-center
                ${
                    selectedShortCode === opt.code
                        ? 'border-gray-800 scale-110 shadow-md'
                        : 'border-transparent hover:scale-105 hover:shadow'
                }
            `}
        >
            {selectedShortCode === opt.code && (
                <span className="text-gray-700 font-bold text-[10px]">✓</span>
            )}
        </button>
    );

    return (
        <Container>
            <h1 className="page-header">User Profile</h1>
            <br />
            <div className="flex justify-center">
                {!userData && !loading ? (
                    <ErrorMessage error={error || 'No user data found.'} />
                ) : (
                    <div className="bg-gray-100 rounded-xl shadow-md p-6 max-w-sm w-full">
                        <div className="flex items-center gap-4 mb-4">
                            <span role="img" aria-label="user">
                                <UserIcon style={{ width: '30px', height: '30px' }} />
                            </span>
                            <h2 className="text-xl font-semibold">{userData?.name || 'N/A'}</h2>
                        </div>

                        {/* User Info Section */}
                        <div className="space-y-2 text-sm text-gray-700 pb-4 border-b border-gray-300 mb-4">
                            <div className="flex items-center gap-2">
                                <UsernameIcon
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                        color: 'var(--pm-primary)',
                                    }}
                                />
                                <span>Username: {userData?.username || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <GradeLevelIcon
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                        color: 'var(--pm-primary)',
                                    }}
                                />
                                <span>Grade Level: {userData?.gradeShortName || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <OrganizationsIcon
                                    style={{
                                        width: '20px',
                                        height: '16px',
                                        color: 'var(--pm-primary)',
                                    }}
                                />
                                <span>School: {userData?.schoolName || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <OrganizationsIcon
                                    style={{
                                        width: '20px',
                                        height: '16px',
                                        color: 'var(--pm-primary)',
                                    }}
                                />
                                <span>District: {userData?.districtName || 'N/A'}</span>
                            </div>

                            <div className="flex justify-center pt-4">
                                <SimpleButton
                                    onClick={() => navigate('/dashboard')}
                                    variant="outlined"
                                >
                                    Back to Dashboard
                                </SimpleButton>
                            </div>
                        </div>

                        {/* Color Selection Section */}
                        <div className="pt-2">
                            <h3 className="text-sm font-semibold mb-3 text-gray-700 text-center">
                                Theme Color
                            </h3>

                            <div className="flex justify-center gap-3 mb-3">
                                {row1.map(renderColorButton)}
                            </div>
                            <div className="flex justify-center gap-3 mb-5">
                                {row2.map(renderColorButton)}
                            </div>

                            <div className="flex justify-center">
                                <SimpleButton onClick={handleColorSubmit} variant="contained">
                                    Apply Theme
                                </SimpleButton>
                            </div>

                            <StatusMessage
                                visible={colorSuccess}
                                status={'Theme updated!'}
                                style={{ textAlign: 'center' }}
                            />
                        </div>
                    </div>
                )}
            </div>
        </Container>
    );
};

export default UserProfilePage;
